import { useEffect } from "react";

import { useHistory } from "react-router";

import { Badge, Banner, Button } from "@alpacahq/ui/old";

import {
  useGetAccountDocumentRequests,
  useGetMargin,
  useGetOwnerDetails,
} from "~/v2/api/hooks";
import { useGetIsLegalEntity } from "~/v2/api/hooks/useGetIsLegalEntity";
import type { Account } from "~/v2/api/rest/account";
import {
  ACCOUNT_STATUS_MAP,
  type BannerAccountStatus,
} from "./AccountStatusMap";

export const AccountStatusBanner = ({
  account,
  refetchAccounts,
}: {
  account: Account;
  refetchAccounts: () => void;
}) => {
  const history = useHistory();

  // Refetch on mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately mount-only: the account list is refetched once when the banner mounts. refetchAccounts is not stable, so listing it would refetch in a loop.
  useEffect(() => {
    refetchAccounts();
  }, []);

  const { isLegalEntity, isLegalEntityLoading } = useGetIsLegalEntity();

  const status = account.status as BannerAccountStatus;

  const { details } = useGetOwnerDetails();

  const { margin } = useGetMargin({
    accountID: account?.id,
    paper: false,
    options: {
      enabled: !!account?.id && status === "ACTIVE",
    },
  });

  const isDocumentRequested = (details?.review_status ?? "") === "PENDING_USER";

  const { data: accountDocumentRequests } = useGetAccountDocumentRequests({
    accountId: account?.id ?? "",
    options: {
      enabled: !!account?.id && isDocumentRequested,
    },
  });

  if (!account || !details || isLegalEntityLoading) {
    return null;
  }

  const bannerProps =
    ACCOUNT_STATUS_MAP[status]?.({
      account,
      details,
      margin: margin ? margin : undefined,
      accountDocumentRequests,
      isLegalEntity,
    }) ?? null;

  // if account status is not in map, show nothing
  if (!bannerProps) {
    return null;
  }

  const { title, message, button, variant, badge } = bannerProps;

  return (
    <Banner
      variant={variant}
      title={title}
      message={message}
      button={
        button ? (
          <Button
            size="v2/small"
            variant="v2/filled"
            onClick={() => history.push(button.to)}
          >
            {button.text}
          </Button>
        ) : badge ? (
          <Badge variant={badge.variant}>{badge.title}</Badge>
        ) : undefined
      }
    />
  );
};
